/**
 * apps/automations-portal/src/features/admin/LedgerLogPage.tsx
 *
 * Two flat, chronological views onto the ledger: every event, and every
 * delivery attempt made off the back of one. Unlike the client threads page,
 * this deliberately shows the raw `activity` string — it's the one place in
 * the portal meant for reading the machine-facing name, not the summary.
 */
import { useEffect, useState } from 'react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { LedgerDelivery, LedgerEventWithClient, LedgerSeverity } from '@/lib/types';

const SEVERITY_VARIANT: Record<LedgerSeverity, BadgeProps['variant']> = {
  INFO: 'secondary',
  ACTION: 'warning',
  MONEY: 'success',
  ERROR: 'destructive',
};

export function LedgerLogPage() {
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Log</h1>
        <p className="text-sm text-muted-foreground">Every event the ledger has recorded, and every delivery sent because of one.</p>
      </div>

      <Tabs defaultValue="events">
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
        </TabsList>
        <TabsContent value="events">
          <EventsTable />
        </TabsContent>
        <TabsContent value="deliveries">
          <DeliveriesTable />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EventsTable() {
  const [events, setEvents] = useState<LedgerEventWithClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    adminApi
      .ledgerLog({ limit: 200 })
      .then(setEvents)
      .catch(() => setError('Could not load the event log. Try refreshing the page.'));
  }, []);

  if (error) return <p className="mt-4 text-sm text-destructive">{error}</p>;
  if (!events) {
    return (
      <div className="mt-4 flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        No events recorded yet.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Activity</TableHead>
            <TableHead>Thread</TableHead>
            <TableHead>Summary</TableHead>
            <TableHead>Severity</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {formatDateTime(event.occurredAt)}
              </TableCell>
              <TableCell>{event.client.name}</TableCell>
              <TableCell className="font-mono text-xs">{event.activity}</TableCell>
              <TableCell className="font-mono text-xs">{event.threadKey}</TableCell>
              <TableCell>{event.summary}</TableCell>
              <TableCell>
                <Badge variant={SEVERITY_VARIANT[event.severity]}>{event.severity}</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DeliveriesTable() {
  const [deliveries, setDeliveries] = useState<LedgerDelivery[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    adminApi
      .ledgerDeliveries({ limit: 200 })
      .then(setDeliveries)
      .catch(() => setError('Could not load deliveries. Try refreshing the page.'));
  }, []);

  if (error) return <p className="mt-4 text-sm text-destructive">{error}</p>;
  if (!deliveries) {
    return (
      <div className="mt-4 flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (deliveries.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        No deliveries recorded yet.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Recipient</TableHead>
            <TableHead>Summary</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries.map((delivery) => (
            <TableRow key={delivery.id}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {formatDateTime(delivery.sentAt ?? delivery.createdAt)}
              </TableCell>
              <TableCell>{delivery.recipient}</TableCell>
              <TableCell>{delivery.event.summary}</TableCell>
              <TableCell>
                <DeliveryStatusBadge delivery={delivery} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DeliveryStatusBadge({ delivery }: { delivery: LedgerDelivery }) {
  if (delivery.sentAt) return <Badge variant="success">Sent</Badge>;
  if (delivery.error) return <Badge variant="destructive">{delivery.error}</Badge>;
  return <Badge variant="secondary">{delivery.providerStatus ?? 'Pending'}</Badge>;
}
